import type React from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import { Alert, Grid, Spacer } from '@zillow/constellation';
import { Text } from '@zillow/constellation-10';
import { propertyUtils } from '@zg-rentals/rental-manager-env';

import { ModularListingContext } from '../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../components/Wizard/context';

import { useSelector } from '../../../selectors/useSelector';

import actions from '../../../containers/CreateListingPage/actions';

import accessUtils from '../../../utils/accessUtils';

import stringHelper from '../../../utils/stringHelper';

import { MODAL_COMPONENT } from '../../../components/WizardModularListing/Review/constants';
import { DISPLAY_SERVER_ERROR_AT } from '../../../constants/WizardConstants';
import StringConstants from '../../../constants/StringConstants';
import { TRIGGER_LOCATIONS } from '../../../constants/ClickstreamConstants';

import ApplicationsCSEvents from '../../../model/ClickstreamEvent/ApplicationsCSEvents';

import WizardAlert from '../../../components/WizardAlert';
import PageWizard from '../../../components/Wizard/PageWizard';
import Modal from '../../../components/WizardModularListing/Review/components/Modal';

import { WizardBody, WizardForm, WizardFormSectionContainer, WizardHeading } from '../../../styles/wizard';

import FastPostReviewContent from './components/FastPostReviewContent';

import type { MouseEvent } from 'react';
import type { GetModalComponentObjArgs, FastPostReviewProps as Props } from './interfaces';

const getModalComponentObj = ({ componentKey, isRoomForRent }: GetModalComponentObjArgs) => {
  const renderedComponent = {
    ...MODAL_COMPONENT[componentKey as keyof typeof MODAL_COMPONENT],
  };

  if (isRoomForRent && componentKey === 'PropertyDescriptionFormField') {
    renderedComponent.title = 'Room and shared space description';
  }

  return renderedComponent;
};

const FastPostReview = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: Props): JSX.Element => {
  const dispatch = useDispatch();

  const {
    // @ts-expect-error
    wizardState: { isFetchingData },
  } = useContext(WizardContext);

  const {
    modularListingState: { hasFieldError, isInModularListingContext, isSaveComplete, isSaveDisabled, serverErrorObj },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const { appConfig, cookieEnabledServices, currentProperty, user } = useSelector((state) => {
    return {
      appConfig: state.app.appConfig,
      cookieEnabledServices: state.app.cookieEnabledServices,
      currentProperty: state.properties.currentProperty,
      user: state.user,
    };
  }, shallowEqual);

  const [clickedNext, setClickedNext] = useState(false);
  const [clickedBackNext, setClickedBack] = useState(false);

  const [componentKey, setComponentKey] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isOpenWithDelay, setIsOpenWithDelay] = useState(false);

  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });

  const isLegacyTourDisabled = accessUtils.isLegacyTourDisabled({ appConfig, cookieEnabledServices });

  useEffect(() => {
    actions.updateShouldSaveOnBlur({ reducerDispatch, shouldSaveOnBlur: false });
    actions.updateHasFieldError({ reducerDispatch, hasFieldError: false });

    return () => actions.updateShouldSaveOnBlur({ reducerDispatch, shouldSaveOnBlur: true });
  }, []);

  useEffect(() => {
    if (isInModularListingContext && hasServerError()) {
      setIsSaving(false);

      // fixes issue where saving on blur happens
      // this will keep the manual saving functionality in review edit modal
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
    }
  }, [isSaveComplete, serverErrorObj.fieldName]);

  useEffect(() => {
    const { fieldName } = serverErrorObj;

    if (!fieldName && !isFetchingData) {
      checkNavToStep();
    }
  }, [isFetchingData]);

  // clicked save cta
  useEffect(() => {
    // isSaveComplete triggers manually saving for the review edit modal state
    setIsSaving(isSaveComplete);

    if (isSaveComplete === false && hasFieldError === false && hasServerError() === false) {
      setIsOpen(false);
    }
  }, [isSaveComplete]);

  useEffect(() => {}, [isSaveDisabled]);

  useEffect(() => {
    actions.updateIsInModalState({ reducerDispatch, isInModalState: isOpen });

    if (isOpen) {
      // we need to account for possible modal state conditional checks
      setTimeout(() => {
        setIsOpenWithDelay(isOpen);
      }, 0);
    } else {
      setIsOpenWithDelay(isOpen);
      setComponentKey('');
    }
  }, [isOpen]);

  const { component: ModalBodyComponent, title: modalTitle = '' } = useMemo(
    () => getModalComponentObj({ componentKey, isRoomForRent }),
    [componentKey],
  );

  const hasServerError = () => {
    return serverErrorObj.fieldName !== '';
  };

  const checkNavToStep = () => {
    if (clickedNext) {
      onNextClick();
    }

    if (clickedBackNext) {
      onBackClick();
    }
  };

  const onNextClick = () => {
    if (isFetchingData) {
      setClickedNext(true);
      return;
    }

    onGoToNextStep();
  };

  const onBackClick = () => {
    if (isFetchingData) {
      setClickedBack(true);
      return;
    }

    onGoToPrevStep();
  };

  const clearFormError = () => {
    actions.updateHasFieldError({ reducerDispatch, hasFieldError: false });
  };

  const clearSaveDisabled = () => {
    actions.updateAnyProperty({
      reducerDispatch,
      data: {
        isSaveDisabled: false,
      },
    });
  };

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const trackEditClick = (groupTitle?: string, fieldTitle?: string) => {
    if (!groupTitle || !fieldTitle) {
      return;
    }

    if (fieldTitle?.toLowerCase().includes('applications')) {
      ApplicationsCSEvents.ClickEditAcceptApplications({
        currentProperty,
        user,
        triggerLocation: TRIGGER_LOCATIONS.POSTING_PATH,
      });
    }
  };

  const handleModalOpen = () => {
    clearServerError();
    clearSaveDisabled();

    // reset save cta
    actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
  };

  const handleEditClick = (event: MouseEvent<HTMLButtonElement>): void => {
    const { parentElement, value: aComponentKey } = event.currentTarget;

    let fieldTitle = parentElement?.firstChild?.textContent
      ? stringHelper.startCaseRemoveSpace(parentElement?.firstChild?.textContent)
      : undefined;

    if (fieldTitle && fieldTitle.includes(StringConstants.ERROR)) {
      fieldTitle = `Incomplete${fieldTitle.substring(
        fieldTitle.indexOf(StringConstants.ERROR) + StringConstants.ERROR.length,
      )}`;
    }

    trackEditClick('FastPost', fieldTitle);

    // IncompletePhone comes from the title passed to AccordionIconError and text from Phone
    if (
      fieldTitle === MODAL_COMPONENT.PhoneNumberFormField.title ||
      fieldTitle === `Incomplete${MODAL_COMPONENT.PhoneNumberFormField.title}`
    ) {
      return;
    }

    setComponentKey(aComponentKey);
    setIsOpen(true);
  };

  const handleModalClose = () => {
    clearFormError();
    clearServerError();
    setIsOpen(false);

    actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
  };

  const handleModalSaveClick = () => {
    // communicate that cta has been clicked
    actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: true });
  };

  const renderComponent = () => {
    if (!ModalBodyComponent) {
      return <div />;
    }

    return (
      <Spacer paddingBottom="xl">
        {/* @ts-expect-error */}
        <ModalBodyComponent closeModal={handleModalClose} />
      </Spacer>
    );
  };

  return (
    <PageWizard
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onNextClick}
      onBackClick={onBackClick}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      {...rest}
    >
      <div data-testid="FastPostReview">
        <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.PAGE} />

        <Grid
          display="grid"
          gridTemplateColumns={{
            default: 'repeat(6, 1fr)',
            // 769
            lg_gte: 'repeat(12, 1fr)',
          }}
          gap="sm"
        >
          <Grid gridColumnEnd={{ default: 'span 6', lg_gte: 'span 12' }}>
            {/* cast as div to prevent cannot appear as a descendant of form error */}
            <WizardForm asChild={true} onSubmit={(e: React.FormEvent<HTMLFormElement>) => e.preventDefault()}>
              <div>
                <WizardFormSectionContainer>
                  <WizardHeading level={1} textStyle="heading-sm">
                    Confirm final details
                  </WizardHeading>

                  <WizardBody>
                    To save you time, we added a few details about your home. You can edit, if needed.
                    {isLegacyTourDisabled && (
                      <Alert
                        marginTop="sm"
                        body={
                          <Text>
                            After you publish your listing, you&apos;ll be able to set your availability for tours.
                          </Text>
                        }
                      />
                    )}
                  </WizardBody>

                  <FastPostReviewContent onEditClick={handleEditClick} />
                </WizardFormSectionContainer>
              </div>
            </WizardForm>
          </Grid>
        </Grid>

        <Modal
          modalTitle={modalTitle}
          isOpen={isOpenWithDelay}
          component={renderComponent()}
          onOpen={handleModalOpen}
          onClose={handleModalClose}
          onSaveClick={handleModalSaveClick}
          isSaveDisabled={isSaveDisabled}
          isSaving={isSaving}
        />
      </div>
    </PageWizard>
  );
};

export default FastPostReview;
